import { isWithinInterval } from 'date-fns'

/** One salary code's aggregated total for a week, formatted as "HH:MM". */
export interface WeeklyActivitySum {
  name: string
  sum: string
}

/** Aggregated work totals for a single calendar week. */
export interface WeeklyWorkSum {
  /** Total work time across the week, formatted as "HH:MM". */
  totalWorkTime: string
  /** Per-salary-code sums, ordered like the month table, formatted as "HH:MM". */
  activitySums: WeeklyActivitySum[]
  /** Whether the week has any work to show (used to hide the info icon). */
  hasEntries: boolean
}

/** Minimal shape of a work-period group needed to compute weekly sums. */
export interface WeeklySumGroup {
  /** Representative date used to place the group in a week (null = skip). */
  date: Date | null
  totalWorkTime: string | null | undefined
  groupedActivitySums: {
    name: string | null
    sum: string | null
    salaryId?: string | null
  }[]
}

export interface WeekInterval {
  start: Date
  end: Date
}

/**
 * Parse a `TimeDelta` scalar ("07:15", "07:15:30", "-02:00") into seconds.
 * Hours are unbounded (the backend never emits a day component) and the sign,
 * when present, applies to the whole value. Returns 0 for unparseable input.
 */
function timedeltaToSeconds(value: string | null | undefined): number {
  if (!value) return 0

  const match = value.match(/^(-)?(\d+):(\d{2})(?::(\d{2}(?:\.\d+)?))?$/)
  if (!match) return 0

  const sign = match[1] ? -1 : 1
  const hours = Number(match[2] ?? 0)
  const minutes = Number(match[3] ?? 0)
  const seconds = Number(match[4] ?? 0)

  return sign * (hours * 3600 + minutes * 60 + seconds)
}

/**
 * Format a signed second count as "HH:MM" (hours may exceed two digits).
 * Seconds are truncated, not rounded, so a weekly total matches the sum of the
 * per-day values shown on the calendar (see `enforceHourMinuteString`).
 */
function secondsToHourMinute(total: number): string {
  const abs = Math.abs(total)
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  // Sub-minute residue truncates to nothing, so never render it as "-00:00".
  const sign = total < 0 && hours + minutes > 0 ? '-' : ''
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}`
}

const ZERO = '00:00'

/**
 * Aggregate work-period groups into per-week totals, aligned by index with the
 * provided week intervals. A group is attributed to the week its `date` — the
 * start of the group — falls in; groups without a date are skipped. Salary-code
 * sums are grouped by name and ordered by salary id like the month table.
 */
export function getWeeklyWorkSums(
  groups: WeeklySumGroup[],
  weeks: WeekInterval[]
): WeeklyWorkSum[] {
  return weeks.map(week => {
    const inWeek = groups.filter(
      group => group.date && isWithinInterval(group.date, week)
    )

    const totalSeconds = inWeek.reduce(
      (acc, group) => acc + timedeltaToSeconds(group.totalWorkTime),
      0
    )

    const byName = new Map<string, { seconds: number; salaryId: string }>()
    inWeek.forEach(group =>
      group.groupedActivitySums.forEach(activitySum => {
        if (!activitySum.name) return
        const existing = byName.get(activitySum.name)
        byName.set(activitySum.name, {
          seconds:
            (existing?.seconds ?? 0) + timedeltaToSeconds(activitySum.sum),
          salaryId: existing?.salaryId ?? activitySum.salaryId ?? '',
        })
      })
    )

    const activitySums: WeeklyActivitySum[] = [...byName.entries()]
      .map(([name, { seconds, salaryId }]) => ({
        name,
        salaryId,
        sum: secondsToHourMinute(seconds),
      }))
      // Codes that cancel out over the week (and sub-minute residue) add noise.
      .filter(activitySum => activitySum.sum !== ZERO)
      .sort(
        (a, b) =>
          a.salaryId.localeCompare(b.salaryId) || a.name.localeCompare(b.name)
      )
      .map(({ name, sum }) => ({ name, sum }))

    const totalWorkTime = secondsToHourMinute(totalSeconds)

    return {
      totalWorkTime,
      activitySums,
      hasEntries:
        inWeek.length > 0 &&
        (totalWorkTime !== ZERO || activitySums.length > 0),
    }
  })
}
